// ─────────────────────────────────────────────────────────────────
// Glass-i — Production Department Execution Flow (vertical timeline,
// drag-to-reorder).
// Stage 6B.8A-3K.6 + 3K.6.1 drag/save + 3K.6.2 vertical redesign.
//
// Vertical pipeline of stages: Cutting → Polish → Fabrication → … →
// Dispatch, rendered as a timeline with a left-side connecting rail.
// Each stage is a card alongside a circular order badge; the badge
// sits ON the rail so the eye reads a single pipeline. Manual /
// Machine alternatives stack as compact chips beneath the parent.
//
// Drag any card up or down to change the production sequence. The
// alternatives stay attached to their parent's bucket and share its
// execution order on save.
//
// Library: @dnd-kit/core + sortable + utilities (MIT, ~17 kB gz,
// pure JS bundle, no CDN or external assets). Pointer + Keyboard
// sensors for accessibility.
// ─────────────────────────────────────────────────────────────────

import { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Empty, Tag, Tooltip, message } from 'antd';
import {
  InfoCircleOutlined, HolderOutlined,
  SaveOutlined, UndoOutlined, CheckOutlined
} from '@ant-design/icons';
import {
  DndContext, KeyboardSensor, PointerSensor, closestCenter,
  useSensor, useSensors
} from '@dnd-kit/core';
import {
  arrayMove, verticalListSortingStrategy, SortableContext,
  sortableKeyboardCoordinates, useSortable
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';

import {
  buildExecutionFlowGroups, DEFAULT_EXECUTION_FLOW,
  executionGroupLabel
} from './productionDepartmentsHelpers';

const WORK_MODE_TAG = {
  manual:         { color: 'blue',    text: 'Manual' },
  machine:        { color: 'gold',    text: 'Machine' },
  mixed:          { color: 'purple',  text: 'Mixed' },
  outsourced:     { color: 'magenta', text: 'Outsourced' },
  not_applicable: { color: 'default', text: 'N/A' }
};

// Soft accent for the rail badge per execution group — purely
// cosmetic, helps the eye distinguish upstream / midstream / QC
// stages at a glance.
const GROUP_ACCENT = {
  cutting:            '#3b82f6',
  polish:             '#0ea5e9',
  fabrication:        '#06b6d4',
  washing:            '#14b8a6',
  tempering:          '#f59e0b',
  lami:               '#a855f7',
  dgu:                '#ec4899',
  design:             '#8b5cf6',
  ready_for_dispatch: '#10b981',
  qc:                 '#22c55e',
  dispatch:           '#16a34a',
  support:            '#94a3b8',
  other:              '#94a3b8'
};

// Renumber a sorted array of groups to 10, 20, 30, … and emit the
// minimum diff against the original orders.
function computeOrderUpdates(groups) {
  const updates = [];
  groups.forEach((g, idx) => {
    const target = (idx + 1) * 10;
    const affected = [g.parent, ...g.alternatives].filter(Boolean);
    for (const dept of affected) {
      if (!dept || !dept.id) continue;
      if (Number(dept.executionOrder) !== target) {
        updates.push({ id: dept.id, executionOrder: target });
      }
    }
  });
  return updates;
}

// ── Sortable card — one execution-order bucket ─────────────────
function FlowCard({ group, index, total, dragDisabled, isLast }) {
  const {
    attributes, listeners, setNodeRef, transform, transition,
    isDragging
  } = useSortable({ id: group.key, disabled: dragDisabled });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.55 : 1,
    zIndex:  isDragging ? 5  : 1
  };

  const parent = group.parent;
  const accent = GROUP_ACCENT[group.group] || GROUP_ACCENT.other;
  // Display a friendly step number (1, 2, 3…) on the badge. The
  // backend execution_order remains 10, 20, 30… (computed in
  // computeOrderUpdates) so future inserts have 10-step gaps.
  const step  = index + 1;
  const order = step * 10;

  return (
    <li ref={setNodeRef} style={style}
        className={`exf-row ${isDragging ? 'is-dragging' : ''}`}>

      {/* Rail column — order badge sits on the rail; the rail is
          drawn as a CSS line so it connects continuously between
          badges, with a small arrow chevron between cards. */}
      <div className="exf-row__rail">
        <span className="exf-row__badge"
              style={{ background: accent, borderColor: accent }}
              title={`Step ${step} · execution order ${order}`}>
          {step}
        </span>
        {!isLast && <span className="exf-row__rail-line" aria-hidden="true" />}
      </div>

      {/* Card body */}
      <div className="exf-row__card">
        <div className="exf-row__head">
          <Tooltip title={dragDisabled
            ? 'Drag-reorder is disabled (loading)'
            : 'Drag to reorder this stage'}>
            <button type="button"
                    className="exf-row__handle"
                    {...attributes}
                    {...listeners}
                    aria-label={`Drag stage ${group.label}`}>
              <HolderOutlined />
            </button>
          </Tooltip>

          <div className="exf-row__title-block">
            <span className="exf-row__title">
              {parent ? parent.departmentName
                      : (executionGroupLabel(group.group) || group.label)}
            </span>
            <span className="exf-row__subtitle">
              {parent && parent.departmentCode
                ? <>
                    <span className="exf-row__group">
                      {executionGroupLabel(group.group) || group.label}
                    </span>
                    <span className="exf-row__sep">·</span>
                    <span className="exf-row__code">{parent.departmentCode}</span>
                  </>
                : <span className="exf-row__group">
                    {executionGroupLabel(group.group) || group.label}
                  </span>}
            </span>
          </div>

          {parent && parent.allowParallelExecution && (
            <Tag color="purple" className="exf-row__pill">
              Parallel-OK
            </Tag>
          )}
          {parent && parent.isQcStage && (
            <Tag icon={<CheckOutlined />} color="green"
                 className="exf-row__pill">
              QC
            </Tag>
          )}
        </div>

        {group.alternatives.length > 0 ? (
          <div className="exf-row__alts">
            {group.alternatives.map((alt) => {
              const tag = WORK_MODE_TAG[alt.workMode]
                        || { color: 'default', text: '—' };
              return (
                <span className="exf-alt"
                      key={alt.id || alt.departmentCode}>
                  <Tag color={tag.color} className="exf-alt__mode">
                    {tag.text}
                  </Tag>
                  <span className="exf-alt__name"
                        title={alt.departmentName}>
                    {alt.departmentName}
                  </span>
                  {alt.departmentCode && (
                    <span className="exf-alt__code">
                      {alt.departmentCode}
                    </span>
                  )}
                </span>
              );
            })}
          </div>
        ) : (
          !parent && (
            <div className="exf-row__alts exf-row__alts--empty">
              No department configured at this stage.
            </div>
          )
        )}
      </div>
    </li>
  );
}

// ────────────────────────────────────────────────────────────────
export default function ExecutionFlowView({
  rows, onReorder, saving
}) {
  const apiGroups = useMemo(() => buildExecutionFlowGroups(rows), [rows]);

  const skeleton = useMemo(() => DEFAULT_EXECUTION_FLOW.map((s) => ({
    order: s.order, group: s.group, label: s.label,
    parent: null, alternatives: []
  })), []);

  const [working, setWorking] = useState([]);
  useEffect(() => {
    const source = apiGroups.length > 0 ? apiGroups : skeleton;
    setWorking(source.map((g, i) => ({
      ...g,
      key: g.parent && g.parent.id
        ? `parent-${g.parent.id}`
        : `bucket-${g.group}-${g.order}-${i}`
    })));
  }, [apiGroups, skeleton]);

  const original = useMemo(() => {
    const source = apiGroups.length > 0 ? apiGroups : skeleton;
    return source.map((g, i) => g.parent && g.parent.id
      ? `parent-${g.parent.id}`
      : `bucket-${g.group}-${g.order}-${i}`);
  }, [apiGroups, skeleton]);

  const dirty = useMemo(() => {
    if (working.length !== original.length) return false;
    return working.some((w, i) => w.key !== original[i]);
  }, [working, original]);

  // IMPORTANT: keep ALL hooks above the early-return below. Adding a
  // hook (useMemo / useState / etc.) after the conditional `return
  // <Empty />` changes the hook count between renders and triggers
  // React's "Rendered more hooks than during the previous render"
  // crash. The dirtyCount hook lived after the early-return in a
  // prior revision; it now sits with its peers.
  const dirtyCount = useMemo(
    () => computeOrderUpdates(working).length, [working]);

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 4 }
    }),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates
    })
  );

  const onDragEnd = (event) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;
    const oldIndex = working.findIndex((g) => g.key === active.id);
    const newIndex = working.findIndex((g) => g.key === over.id);
    if (oldIndex < 0 || newIndex < 0) return;
    setWorking((prev) => arrayMove(prev, oldIndex, newIndex));
  };

  const onReset = () => {
    const source = apiGroups.length > 0 ? apiGroups : skeleton;
    setWorking(source.map((g, i) => ({
      ...g,
      key: g.parent && g.parent.id
        ? `parent-${g.parent.id}`
        : `bucket-${g.group}-${g.order}-${i}`
    })));
  };

  const onSave = async () => {
    const updates = computeOrderUpdates(working);
    if (updates.length === 0) {
      message.info('Nothing to save — order is unchanged.');
      return;
    }
    if (!onReorder) {
      message.error('Save handler not wired.');
      return;
    }
    try {
      await onReorder(updates);
    } catch (e) {
      message.error(e?.response?.data?.error?.message
        || 'Failed to save the new execution order.');
    }
  };

  if (!Array.isArray(rows) && apiGroups.length === 0) {
    return (
      <Empty description="Loading execution flow…"
             style={{ padding: 24 }} />
    );
  }

  const dragDisabled = !!saving || apiGroups.length === 0;
  const stagesCount  = working.length;

  return (
    <div className="exf-shell">
      <div className="exf-toolbar">
        <div className="exf-toolbar__lead">
          <Alert
            type="info"
            showIcon
            icon={<InfoCircleOutlined />}
            message="Drag any stage up or down to change the production sequence. Manual / Machine alternatives stay attached to their parent stage and share the same order."
            className="exf-toolbar__alert"
          />
        </div>
        <div className="exf-toolbar__actions">
          <span className="exf-toolbar__meta">
            <strong>{stagesCount}</strong> stages
            {dirty && (
              <>
                <span className="exf-toolbar__sep">·</span>
                <span className="exf-toolbar__dirty">
                  {dirtyCount} pending change{dirtyCount === 1 ? '' : 's'}
                </span>
              </>
            )}
          </span>
          <Button size="small" icon={<UndoOutlined />}
            onClick={onReset} disabled={!dirty || !!saving}>
            Reset
          </Button>
          <Button size="small" type="primary" icon={<SaveOutlined />}
            onClick={onSave} disabled={!dirty || !!saving}
            loading={!!saving}>
            Save Order
          </Button>
        </div>
      </div>

      {apiGroups.length === 0 && (
        <Alert type="warning" showIcon
          message="Execution flow not loaded — showing the default sequence as a preview. Reorder is disabled until the API returns."
          className="exf-toolbar__alert"
          style={{ marginBottom: 8 }} />
      )}

      <DndContext sensors={sensors}
        collisionDetection={closestCenter}
        onDragEnd={onDragEnd}>
        <SortableContext
          items={working.map((g) => g.key)}
          strategy={verticalListSortingStrategy}>
          <ol className="exf-flow">
            {working.map((g, i) => (
              <FlowCard
                key={g.key}
                group={g}
                index={i}
                total={working.length}
                isLast={i === working.length - 1}
                dragDisabled={dragDisabled} />
            ))}
          </ol>
        </SortableContext>
      </DndContext>
    </div>
  );
}
